import classNames from "classnames";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { assertPasswordMatch, Button, HelperText, PasswordInput, PasswordStrength, PasswordValidationRegex, Spinner } from "../../components";
/**
 * @typedef {{ password: string }} ResetPasswordSubmission
 *
 * @typedef {object} ResetPasswordProps
 * @property {{ password: boolean }} [errors]
 * @property {React.FC<{ href: string, className?: any }>} Link
 * @property {{ forgotPassword: string }} [links]
 * @property {(value: ResetPasswordSubmission) => Promise<any>} onSubmit
 * @property {any} [className]
 */

/**
 * @type {React.FC<ResetPasswordProps & { [key: string]: any }>}
 */

export const ResetPassword = ({
  className,
  onSubmit,
  children,
  errors
}) => {
  /** @type {ReactState<boolean>} */
  const [isLoading, setIsLoading] = useState(false);
  /** @type {ReactState<number>} */

  const [count, setCount] = useState(1);
  const [password, setPassword] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const {
    handleSubmit,
    register,
    errors: formErrors,
    formState,
    getValues,
    reset
  } = useForm({
    mode: "onChange",
    defaultValues: {
      password: "",
      confirm_password: ""
    }
  });
  const {
    isValid
  } = formState;
  /** @param {ResetPasswordSubmission} values */

  const onFormSubmit = async values => {
    setIsLoading(true);
    return onSubmit(values).finally(() => {
      setIsLoading(false);
      reset();
      setCount(count + 1);
      setPassword("");
      setShowConfirm(false);
    });
  };

  return /*#__PURE__*/React.createElement("div", {
    className: classNames("bg-white rounded p-8 shadow", className)
  }, /*#__PURE__*/React.createElement("div", {
    className: "text-3xl text-center font-bold py-4 text-en-gray-800"
  }, "Change Your Password"), /*#__PURE__*/React.createElement("div", null, children), /*#__PURE__*/React.createElement("form", {
    name: "reset-password",
    onSubmit: handleSubmit(onFormSubmit)
  }, /*#__PURE__*/React.createElement("div", {
    className: "py-2"
  }, /*#__PURE__*/React.createElement(PasswordInput, {
    key: `rp-password-${count}`,
    id: "rp-password",
    name: "password",
    placeholder: "New Password",
    className: "w-full",
    valid: !formErrors.password && !errors?.password,
    onChange: e => setPassword(e?.target?.value),
    onToggle: type => setShowConfirm(type === "password"),
    autoComplete: "new-password",
    type: "text",
    ref: register({
      required: "Please provide your New Password",
      pattern: {
        value: PasswordValidationRegex,
        message: ""
      }
    })
  }), password ? /*#__PURE__*/React.createElement(PasswordStrength, {
    password: password
  }) : null, /*#__PURE__*/React.createElement("div", {
    className: "text-left"
  }, formErrors.password ? /*#__PURE__*/React.createElement(HelperText, {
    valid: false
  }, formErrors.password.message) : null)), showConfirm ? /*#__PURE__*/React.createElement("div", {
    className: "py-2"
  }, /*#__PURE__*/React.createElement(PasswordInput, {
    key: `rp-confirm-password-${count}`,
    id: "rp-confirm-password",
    name: "confirm_password",
    placeholder: "Confirm Password",
    className: "w-full",
    valid: !formErrors.confirm_password,
    autoComplete: "confirm-password",
    ref: register({
      required: "Please confirm your New Password",
      validate: value => {
        return assertPasswordMatch(value, getValues("password"), "Passwords do not match");
      }
    })
  }), /*#__PURE__*/React.createElement("div", {
    className: "text-left"
  }, formErrors.confirm_password ? /*#__PURE__*/React.createElement(HelperText, {
    valid: false
  }, formErrors.confirm_password.message) : null)) : null, /*#__PURE__*/React.createElement("div", {
    className: "py-4 text-center"
  }, /*#__PURE__*/React.createElement(Button, {
    className: {
      "mt-4 px-8": true,
      "px-4": false
    },
    disabled: isLoading || !isValid
  }, isLoading ? /*#__PURE__*/React.createElement(Spinner, {
    size: "1.1rem",
    className: "p-0 -mb-1"
  }) : "Continue"))));
};
ResetPassword.defaultProps = {
  className: "block w-full",
  onSubmit: async () => {},
  errors: {
    password: false
  }
};
ResetPassword.propTypes = {
  className: PropTypes.any,
  onSubmit: PropTypes.func,
  children: PropTypes.any,
  errors: PropTypes.any
};